import * as Y from 'yjs';
import type { BoardDoc } from './doc';
import type { CardAssignee, CardItem, CardStatus, Item, Point, Rect, StickyItem } from './types';

export const CARD_W = 280;
export const CARD_H = 150;
export const CARD_STATUSES: readonly CardStatus[] = ['todo', 'doing', 'done'];

/** Stripe colors: saturated, so the stripe reads on a white or a dark card alike. */
export const CARD_COLORS = ['#5B4CF0', '#0090FF', '#12A594', '#30A46C', '#F5A524', '#F76B15', '#E5484D', '#D6409F', '#AB4ABA', '#6B7280'] as const;
export const DEFAULT_CARD_COLOR = CARD_COLORS[0];

export const STATUS_COLORS: Record<CardStatus, string> = { todo: '#6B7280', doing: '#0090FF', done: '#30A46C' };
export const OVERDUE_COLOR = '#E5484D';

export const MAX_TAGS = 12;
export const MAX_TAG_LENGTH = 24;

/** Card geometry in board units, shared by the renderer and hit testing. */
export const CARD_LAYOUT = {
  stripe: 6,
  pad: 14,
  radius: 8,
  box: 18,
  boxGap: 9,
  titleSize: 16,
  descSize: 13,
  lineHeight: 1.3,
  footer: 22,
  chipSize: 11.5,
  chipPad: 7,
  chipGap: 5,
  avatar: 22,
};

const L = CARD_LAYOUT;

export function makeCard(p: Partial<Omit<CardItem, 'type'>> & { id: string; x: number; y: number; z: string }): CardItem {
  return {
    type: 'card',
    w: CARD_W,
    h: CARD_H,
    text: '',
    description: '',
    tags: [],
    status: 'todo',
    color: DEFAULT_CARD_COLOR,
    ...p,
  };
}

export function nextStatus(s: CardStatus): CardStatus {
  return s === 'todo' ? 'doing' : s === 'doing' ? 'done' : 'todo';
}

/** Today in local time as YYYY-MM-DD (the format of `due`). */
export function todayISO(now = new Date()): string {
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${m}-${d}`;
}

export function isValidDate(s: string | undefined): s is string {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
}

/** Past its due date and not done. A card due today is not overdue yet. */
export function isOverdue(card: Pick<CardItem, 'due' | 'status'>, today = todayISO()): boolean {
  return card.status !== 'done' && isValidDate(card.due) && card.due < today;
}

/** "16 окт." / "Oct 16"; the year only when it is not the current one. */
export function formatDue(iso: string, lang: string, today = todayISO()): string {
  if (!isValidDate(iso)) return '';
  const [y, m, d] = iso.split('-').map(Number);
  const sameYear = iso.slice(0, 4) === today.slice(0, 4);
  return new Intl.DateTimeFormat(lang || 'ru', { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) }).format(new Date(y, m - 1, d));
}

export function normalizeTag(raw: string): string {
  return raw.trim().replace(/^#+/, '').replace(/\s+/g, ' ').trim().slice(0, MAX_TAG_LENGTH).trim();
}

/** Adds a tag unless it is empty, a case-insensitive duplicate, or over the limit. */
export function addTag(tags: readonly string[], raw: string): string[] {
  const tag = normalizeTag(raw);
  if (!tag || tags.length >= MAX_TAGS || tags.some((t) => t.toLowerCase() === tag.toLowerCase())) return [...tags];
  return [...tags, tag];
}

export function removeTag(tags: readonly string[], tag: string): string[] {
  return tags.filter((t) => t !== tag);
}

/** A stable color per tag, so the same tag looks the same on every card. */
export function tagColor(tag: string): string {
  let h = 0;
  for (const ch of tag.toLowerCase()) h = (h * 31 + ch.codePointAt(0)!) >>> 0;
  return CARD_COLORS[h % CARD_COLORS.length];
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return [...parts[0]].slice(0, 2).join('').toUpperCase();
  return ([...parts[0]][0] + [...parts[1]][0]).toUpperCase();
}

/** The status checkbox in world coordinates (with a little slack for clicking). */
export function checkboxRect(card: Pick<CardItem, 'x' | 'y'>, slack = 4): Rect {
  return { x: card.x + L.stripe + L.pad - slack, y: card.y + L.pad - slack, w: L.box + slack * 2, h: L.box + slack * 2 };
}

export function hitsCheckbox(card: CardItem, p: Point): boolean {
  const r = checkboxRect(card);
  return p.x >= r.x && p.y >= r.y && p.x <= r.x + r.w && p.y <= r.y + r.h;
}

// ---------------------------------------------------------------------------------------------
// Text layout

export type MeasureWidth = (text: string) => number;

/**
 * Greedy word wrap into at most `maxLines` lines no wider than `width`; the last line ends with
 * an ellipsis when text is left over. Words longer than a line are broken between characters.
 */
export function clampLines(text: string, width: number, maxLines: number, measure: MeasureWidth): string[] {
  if (maxLines <= 0 || width <= 0) return [];
  const lines: string[] = [];
  const paragraphs = text.replace(/\r/g, '').split('\n');
  let truncated = false;
  outer: for (let p = 0; p < paragraphs.length; p++) {
    const words = paragraphs[p].split(/\s+/).filter(Boolean);
    let line = '';
    for (let i = 0; i < words.length; i++) {
      let word = words[i];
      const candidate = line ? `${line} ${word}` : word;
      if (measure(candidate) <= width) {
        line = candidate;
        continue;
      }
      if (line) {
        lines.push(line);
        line = '';
        if (lines.length === maxLines) {
          truncated = true;
          break outer;
        }
      }
      // A word wider than the whole line: split it.
      while (measure(word) > width) {
        let cut = fitPrefix(word, width, measure);
        if (cut === 0) cut = 1;
        lines.push(word.slice(0, cut));
        word = word.slice(cut);
        if (lines.length === maxLines) {
          truncated = word.length > 0 || i < words.length - 1 || p < paragraphs.length - 1;
          break outer;
        }
      }
      line = word;
    }
    if (line) lines.push(line);
    if (lines.length === maxLines) {
      truncated = p < paragraphs.length - 1 && paragraphs.slice(p + 1).some((s) => s.trim());
      break;
    }
  }
  if (truncated && lines.length) {
    const last = lines.length - 1;
    let s = lines[last];
    while (s && measure(`${s}…`) > width) s = s.slice(0, -1);
    lines[last] = `${s.trimEnd()}…`;
  }
  return lines;
}

function fitPrefix(s: string, width: number, measure: MeasureWidth): number {
  let lo = 0, hi = s.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (measure(s.slice(0, mid)) <= width) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

export interface CardLayout {
  box: Rect;
  title: { x: number; y: number; w: number; lines: string[] };
  desc: { x: number; y: number; w: number; lines: string[] };
  /** Footer row: vertical center and the pieces placed on it (all relative to the card). */
  footer: {
    y: number;
    avatar: { cx: number; cy: number; r: number } | null;
    /** The linked issue's key (PAY-12). */
    issue: { x: number; w: number; label: string } | null;
    due: { x: number; w: number; label: string } | null;
    chips: { x: number; w: number; label: string }[];
    more: { x: number; w: number; label: string } | null;
  } | null;
}

export interface LayoutMeasure {
  title: MeasureWidth;
  desc: MeasureWidth;
  chip: MeasureWidth;
}

/** Where everything on a card goes, relative to its top-left corner. */
export function layoutCard(card: CardItem, m: LayoutMeasure, dueLabel: string): CardLayout {
  const left = L.stripe + L.pad;
  const right = card.w - L.pad;
  const box = { x: left, y: L.pad, w: L.box, h: L.box };
  const textX = left + L.box + L.boxGap;
  const textW = Math.max(0, right - textX);
  const hasFooter = !!card.issue || !!card.assignee || !!dueLabel || card.tags.length > 0;
  const footerY = card.h - L.pad + 2 - L.footer;
  const footerFits = hasFooter && footerY >= L.pad + L.box;
  const bottom = footerFits ? footerY - 6 : card.h - L.pad + 2;

  const titleLH = L.titleSize * L.lineHeight;
  const descLH = L.descSize * L.lineHeight;
  const titleY = L.pad - (titleLH - L.box) / 2;
  const titleMax = Math.max(1, Math.min(2, Math.floor((bottom - titleY) / titleLH)));
  const titleLines = clampLines(card.text, textW, titleMax, m.title);
  const descY = titleY + Math.max(1, titleLines.length) * titleLH + 3;
  const descMax = Math.max(0, Math.min(2, Math.floor((bottom - descY) / descLH)));
  const descLines = clampLines(card.description, textW, descMax, m.desc);

  let footer: CardLayout['footer'] = null;
  if (footerFits) {
    const cy = footerY + L.footer / 2;
    let end = right;
    let avatar: NonNullable<CardLayout['footer']>['avatar'] = null;
    if (card.assignee) {
      avatar = { cx: right - L.avatar / 2, cy, r: L.avatar / 2 };
      end = right - L.avatar - 8;
    }
    let x = left;
    let issue: NonNullable<CardLayout['footer']>['issue'] = null;
    if (card.issue?.key) {
      const w = m.chip(card.issue.key) + L.chipPad * 2;
      if (x + w <= end) {
        issue = { x, w, label: card.issue.key };
        x += w + L.chipGap;
      }
    }
    let due: NonNullable<CardLayout['footer']>['due'] = null;
    if (dueLabel) {
      const w = m.chip(dueLabel) + L.chipPad * 2;
      if (x + w <= end) {
        due = { x, w, label: dueLabel };
        x += w + L.chipGap;
      }
    }
    const chips: { x: number; w: number; label: string }[] = [];
    let more: NonNullable<CardLayout['footer']>['more'] = null;
    const tags = card.tags;
    for (let i = 0; i < tags.length; i++) {
      const w = m.chip(tags[i]) + L.chipPad * 2;
      const rest = tags.length - i - 1;
      const reserve = rest > 0 ? m.chip(`+${rest}`) + L.chipPad * 2 + L.chipGap : 0;
      if (x + w + reserve <= end) {
        chips.push({ x, w, label: tags[i] });
        x += w + L.chipGap;
        continue;
      }
      const label = `+${tags.length - i}`;
      const mw = m.chip(label) + L.chipPad * 2;
      if (x + mw <= end) more = { x, w: mw, label };
      break;
    }
    footer = { y: footerY, avatar, issue, due, chips, more };
  }

  return { box, title: { x: textX, y: titleY, w: textW, lines: titleLines }, desc: { x: textX, y: descY, w: textW, lines: descLines }, footer };
}

// ---------------------------------------------------------------------------------------------
// Board queries and commands

/** The stripe color that best matches a sticky note color. */
const STICKY_TO_CARD: Record<string, string> = {
  '#FFF3A3': '#F5A524',
  '#FFE066': '#F5A524',
  '#FFC98B': '#F76B15',
  '#FFB3BA': '#E5484D',
  '#F7B5E4': '#D6409F',
  '#D9C5FF': '#AB4ABA',
  '#B8D4FF': '#0090FF',
  '#A9EDE6': '#12A594',
  '#C3EFA6': '#30A46C',
  '#E9F59E': '#30A46C',
  '#E4E6EB': '#6B7280',
  '#3A3D46': '#6B7280',
};

/** A card in place of a sticky: same id (connectors and comment pins stay attached), same spot. */
export function stickyToCard(s: StickyItem): CardItem {
  const [first, ...rest] = s.text.replace(/\r/g, '').split('\n');
  return makeCard({
    id: s.id,
    x: s.x,
    y: s.y,
    w: Math.max(160, s.w),
    h: Math.max(110, Math.round(s.h * 0.75)),
    z: s.z,
    locked: s.locked,
    text: first.trim(),
    description: rest.join('\n').trim(),
    color: STICKY_TO_CARD[s.color.toUpperCase()] ?? DEFAULT_CARD_COLOR,
  });
}

/** Replaces the given stickies with cards in one undoable transaction; returns the ids converted. */
export function convertStickiesToCards(doc: BoardDoc, ids: Iterable<string>): string[] {
  const done: string[] = [];
  doc.transact(() => {
    for (const id of ids) {
      const it = doc.get(id);
      if (it?.type !== 'sticky' || it.locked) continue;
      doc.create(stickyToCard(it));
      done.push(id);
    }
  });
  return done;
}

/** The live Y.Text behind a card field ('text' for the title, 'description'). */
export function cardText(doc: BoardDoc, id: string, key: 'text' | 'description'): Y.Text | undefined {
  const t = doc.items.get(id)?.get(key);
  return t instanceof Y.Text ? t : undefined;
}

export interface TaskGroups {
  todo: CardItem[];
  doing: CardItem[];
  done: CardItem[];
}

/** Cards assigned to a user, grouped by status: overdue first, then by due date, then title. */
export function tasksFor(items: Iterable<Item>, userId: string, today = todayISO()): TaskGroups {
  const groups: TaskGroups = { todo: [], doing: [], done: [] };
  for (const it of items) if (it.type === 'card' && it.assignee?.id === userId) groups[it.status]?.push(it);
  const rank = (c: CardItem) => (isOverdue(c, today) ? 0 : 1);
  const cmp = (a: CardItem, b: CardItem) =>
    rank(a) - rank(b) || (a.due ?? '9999').localeCompare(b.due ?? '9999') || a.text.localeCompare(b.text) || (a.id < b.id ? -1 : 1);
  for (const k of CARD_STATUSES) groups[k].sort(cmp);
  return groups;
}

export function sameAssignee(a: CardAssignee | undefined, b: CardAssignee | undefined): boolean {
  return a?.id === b?.id && a?.name === b?.name && a?.color === b?.color;
}
